import React from 'react';
import { motion } from 'framer-motion';
import { SunIcon, MoonIcon, StarIcon, LockIcon, SparklesIcon, CalendarIcon, ClockIcon, MapPinIcon, ArrowRightIcon, HeartIcon, TargetIcon, BriefcaseIcon, TrendingUpIcon, ActivityIcon } from 'lucide-react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Card } from '../ui/Card';
import { useCart } from '../../contexts/CartContext';
type ServiceType = 'natal' | 'solar' | 'compatibility' | 'combo' | 'forecast';
interface BetaReportPreviewProps {
  serviceType: ServiceType;
  onNavigate?: (page: string) => void;
}
// ═══════════════════════════════════════════
// SERVICE-SPECIFIC CONFIG
// ═══════════════════════════════════════════
const SERVICE_CONFIG: Record<ServiceType, {
  title: string;
  subtitle: string;
  gradient: string;
  accentColor: string;
  person2?: {
    name: string;
    date: string;
    time: string;
    place: string;
  };
  extraInfo?: string;
  summary: string;
  lockedSections: string[];
}> = {
  natal: {
    title: 'Натальная карта',
    subtitle: 'Персональный астрологический анализ',
    gradient: 'from-slate-900 to-slate-800',
    accentColor: 'indigo',
    summary: `Ваша натальная карта показывает яркую и многогранную личность. Солнце во Льве наделяет вас природной харизмой, уверенностью и стремлением к самовыражению. Вы прирождённый лидер, который любит быть в центре внимания и вдохновлять окружающих.

Луна в Скорпионе добавляет глубину вашему внутреннему миру. За внешней уверенностью скрывается интенсивная эмоциональная жизнь. Вы обладаете сильной интуицией и способностью видеть скрытые мотивы людей.

Асцендент в Стрельце формирует ваш внешний образ — оптимистичный, открытый и любознательный. Люди воспринимают вас как человека с широким кругозором и философским взглядом на жизнь.`,
    lockedSections: ['Планеты в знаках зодиака', 'Планеты в домах гороскопа', 'Аспекты между планетами', 'Карьера и финансовый потенциал', 'Любовь и отношения', 'Кармические задачи', 'Таланты и предназначение', 'Рекомендации и компенсаторика']
  },
  solar: {
    title: 'Соляр (Прогноз на год)',
    subtitle: 'Краткий обзор тенденций 2026 года',
    gradient: 'from-slate-900 to-amber-900/80',
    accentColor: 'amber',
    extraInfo: 'Солярный год: 2026',
    summary: `2026 год станет для вас периодом значительного карьерного роста и профессиональной трансформации. Юпитер в 10 доме соляра открывает двери для повышения, признания и расширения влияния в вашей сфере.

Первая половина года (особенно март-май) будет наиболее активной и результативной. Это время для смелых шагов и запуска новых проектов.

В сфере отношений год обещает быть более спокойным, фокус внимания будет смещен на социальную реализацию и достижение материальных целей.`,
    lockedSections: ['Подробный анализ темы года', 'Помесячный прогноз (12 месяцев)', 'Карьера и финансы в этом году', 'Отношения и личная жизнь', 'Здоровье и энергетика', 'Календарь ключевых дат', 'Анализ солярных аспектов', 'Практические рекомендации на год']
  },
  compatibility: {
    title: 'Совместимость (Синастрия)',
    subtitle: 'Базовый анализ отношений',
    gradient: 'from-slate-900 to-rose-900/80',
    accentColor: 'rose',
    person2: {
      name: 'Мария Иванова',
      date: '23 марта 1994',
      time: '09:15',
      place: 'Санкт-Петербург'
    },
    summary: `Ваша пара обладает высоким потенциалом для глубоких, романтичных отношений. Обе Луны находятся в водных знаках (Скорпион и Рак), что создаёт невероятно сильную эмоциональную связь и интуитивное понимание друг друга.

Солнце во Льве и Солнце в Рыбах могут иметь разные подходы к самовыражению, но это создает интересный баланс между яркостью одного партнера и глубиной другого.

Главный вызов для вашей пары — научиться балансировать потребность в слиянии с необходимостью личного пространства.`,
    lockedSections: ['Детальный анализ совместимости', 'Сексуальная совместимость', 'Коммуникация и решение конфликтов', 'Быт, финансы и совместные цели', 'Кармические связи между вами', 'Таблица всех аспектов', 'Долгосрочные перспективы', 'Рекомендации для гармонии']
  },
  combo: {
    title: 'Комбо: Натал + Соляр',
    subtitle: 'Базовый анализ личности и года',
    gradient: 'from-slate-900 to-purple-900/80',
    accentColor: 'purple',
    extraInfo: 'Солярный год: 2026',
    summary: `Ваша натальная карта показывает яркую, харизматичную личность (Солнце во Льве) с глубоким эмоциональным миром (Луна в Скорпионе). Вы обладаете огромным потенциалом для лидерства и влияния на других людей.

В 2026 году эти врожденные качества получат мощную поддержку от транзитного Юпитера. Год будет сфокусирован на карьерном росте и социальной реализации.

Это редкое сочетание личного потенциала и благоприятных внешних обстоятельств, которое бывает раз в 12 лет.`,
    lockedSections: ['Полный разбор натальной карты', 'Все планеты, дома и аспекты', 'Кармические задачи и таланты', 'Детальный прогноз на 2026 год', 'Помесячный календарь событий', 'Интегрированный анализ', 'Финансы и отношения (Натал + Соляр)', 'Персональные рекомендации']
  },
  forecast: {
    title: 'Прогноз (Транзиты)',
    subtitle: 'Краткий обзор текущих влияний',
    gradient: 'from-slate-900 to-teal-900/80',
    accentColor: 'teal',
    extraInfo: 'Период: Март 2026 — Сентябрь 2026',
    summary: `Ближайшие месяцы принесут значительные перемены в вашу жизнь. Главным драйвером изменений выступает транзитный Юпитер, проходящий через ваш 10 дом карьеры. Это открывает окно возможностей для профессионального роста.

Одновременно Сатурн в 7 доме требует серьезного и ответственного подхода к партнерским отношениям. Возможны проверки на прочность существующих связей.

Уран в 1 доме продолжает процесс вашей личной трансформации, подталкивая к обновлению имиджа и способов самовыражения.`,
    lockedSections: ['Подробный анализ всех транзитов', 'Влияние медленных планет', 'Влияние быстрых планет', 'Помесячный прогноз', 'Карьерные и финансовые тренды', 'Прогноз по отношениям', 'Календарь благоприятных дней', 'Зоны риска и предупреждения']
  }
};
export function BetaReportPreview({
  serviceType,
  onNavigate
}: BetaReportPreviewProps) {
  const {
    addItem
  } = useCart();
  const config = SERVICE_CONFIG[serviceType];
  // ═══════════════════════════════════════════
  // HIGHLIGHT SECTIONS (Service-specific)
  // ═══════════════════════════════════════════
  const renderNatalHighlights = () => <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-17039">
      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.1
    }} className="bg-amber-50 rounded-xl p-4 border border-amber-100" data-id="element-17040">
        <div className="flex items-center gap-2 mb-2" data-id="element-17041">
          <SunIcon className="w-5 h-5 text-amber-500" data-id="element-17042" />
          <span className="text-xs font-bold text-amber-600 uppercase tracking-wider" data-id="element-17043">
            Солнце
          </span>
        </div>
        <div className="text-2xl font-bold text-slate-900 flex items-center gap-2" data-id="element-17044">
          <span className="text-amber-500" data-id="element-17045">♌</span> Лев
        </div>
        <div className="text-xs text-slate-500 mt-1" data-id="element-17046">Огонь • Ядро личности</div>
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.2
    }} className="bg-indigo-50 rounded-xl p-4 border border-indigo-100" data-id="element-17047">
        <div className="flex items-center gap-2 mb-2" data-id="element-17048">
          <MoonIcon className="w-5 h-5 text-indigo-500" data-id="element-17049" />
          <span className="text-xs font-bold text-indigo-600 uppercase tracking-wider" data-id="element-17050">
            Луна
          </span>
        </div>
        <div className="text-2xl font-bold text-slate-900 flex items-center gap-2" data-id="element-17051">
          <span className="text-indigo-500" data-id="element-17052">♏</span> Скорпион
        </div>
        <div className="text-xs text-slate-500 mt-1" data-id="element-17053">Вода • Внутренний мир</div>
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.3
    }} className="bg-purple-50 rounded-xl p-4 border border-purple-100" data-id="element-17054">
        <div className="flex items-center gap-2 mb-2" data-id="element-17055">
          <StarIcon className="w-5 h-5 text-purple-500" data-id="element-17056" />
          <span className="text-xs font-bold text-purple-600 uppercase tracking-wider" data-id="element-17057">
            Асцендент
          </span>
        </div>
        <div className="text-2xl font-bold text-slate-900 flex items-center gap-2" data-id="element-17058">
          <span className="text-purple-500" data-id="element-17059">♐</span> Стрелец
        </div>
        <div className="text-xs text-slate-500 mt-1" data-id="element-17060">
          Огонь • Социальная маска
        </div>
      </motion.div>
    </div>;
  const renderSolarHighlights = () => <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-17061">
      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.1
    }} className="bg-amber-50 rounded-xl p-4 border border-amber-100" data-id="element-17062">
        <div className="flex items-center gap-2 mb-2" data-id="element-17063">
          <TargetIcon className="w-5 h-5 text-amber-500" data-id="element-17064" />
          <span className="text-xs font-bold text-amber-600 uppercase tracking-wider" data-id="element-17065">
            Тема года
          </span>
        </div>
        <div className="text-xl font-bold text-slate-900" data-id="element-17066">Трансформация</div>
        <div className="text-xs text-slate-500 mt-1" data-id="element-17067">Карьера и статус</div>
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.2
    }} className="bg-orange-50 rounded-xl p-4 border border-orange-100" data-id="element-17068">
        <div className="flex items-center gap-2 mb-2" data-id="element-17069">
          <StarIcon className="w-5 h-5 text-orange-500" data-id="element-17070" />
          <span className="text-xs font-bold text-orange-600 uppercase tracking-wider" data-id="element-17071">
            Управитель
          </span>
        </div>
        <div className="text-xl font-bold text-slate-900" data-id="element-17072">Юпитер</div>
        <div className="text-xs text-slate-500 mt-1" data-id="element-17073">
          Расширение возможностей
        </div>
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.3
    }} className="bg-emerald-50 rounded-xl p-4 border border-emerald-100" data-id="element-17074">
        <div className="flex items-center gap-2 mb-2" data-id="element-17075">
          <TrendingUpIcon className="w-5 h-5 text-emerald-500" data-id="element-17076" />
          <span className="text-xs font-bold text-emerald-600 uppercase tracking-wider" data-id="element-17077">
            Топ период
          </span>
        </div>
        <div className="text-xl font-bold text-slate-900" data-id="element-17078">Март — Май</div>
        <div className="text-xs text-slate-500 mt-1" data-id="element-17079">Максимум энергии</div>
      </motion.div>
    </div>;
  const renderCompatibilityHighlights = () => <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-17080">
      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.1
    }} className="bg-rose-50 rounded-xl p-4 border border-rose-100 text-center" data-id="element-17081">
        <div className="text-xs font-bold text-rose-600 uppercase tracking-wider mb-2" data-id="element-17082">
          Эмоции
        </div>
        <div className="text-3xl font-bold text-rose-500 mb-1" data-id="element-17083">85%</div>
        <div className="text-xs text-slate-500" data-id="element-17084">Высокая совместимость</div>
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.2
    }} className="bg-pink-50 rounded-xl p-4 border border-pink-100 text-center" data-id="element-17085">
        <div className="text-xs font-bold text-pink-600 uppercase tracking-wider mb-2" data-id="element-17086">
          Романтика
        </div>
        <div className="text-3xl font-bold text-pink-500 mb-1" data-id="element-17087">92%</div>
        <div className="text-xs text-slate-500" data-id="element-17088">Отличное притяжение</div>
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.3
    }} className="bg-sky-50 rounded-xl p-4 border border-sky-100 text-center" data-id="element-17089">
        <div className="text-xs font-bold text-sky-600 uppercase tracking-wider mb-2" data-id="element-17090">
          Общение
        </div>
        <div className="text-3xl font-bold text-sky-500 mb-1" data-id="element-17091">78%</div>
        <div className="text-xs text-slate-500" data-id="element-17092">Хорошее понимание</div>
      </motion.div>
    </div>;
  const renderComboHighlights = () => <div className="grid grid-cols-2 gap-4" data-id="element-17093">
      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.1
    }} className="bg-indigo-50 rounded-xl p-4 border border-indigo-100" data-id="element-17094">
        <div className="text-xs font-bold text-indigo-600 uppercase tracking-wider mb-1" data-id="element-17095">
          Личность
        </div>
        <div className="text-lg font-bold text-slate-900" data-id="element-17096">
          ♌ Лев / ♏ Скорпион
        </div>
      </motion.div>
      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.2
    }} className="bg-amber-50 rounded-xl p-4 border border-amber-100" data-id="element-17097">
        <div className="text-xs font-bold text-amber-600 uppercase tracking-wider mb-1" data-id="element-17098">
          Тема 2026
        </div>
        <div className="text-lg font-bold text-slate-900" data-id="element-17099">Карьерный рост</div>
      </motion.div>
    </div>;
  const renderForecastHighlights = () => <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-17100">
      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.1
    }} className="bg-amber-50 rounded-xl p-4 border border-amber-100" data-id="element-17101">
        <div className="flex items-center gap-2 mb-2" data-id="element-17102">
          <div className="w-6 h-6 rounded-full bg-amber-200 text-amber-700 flex items-center justify-center font-bold" data-id="element-17103">
            ♃
          </div>
          <span className="text-xs font-bold text-amber-600 uppercase tracking-wider" data-id="element-17104">
            Юпитер
          </span>
        </div>
        <div className="text-lg font-bold text-slate-900" data-id="element-17105">в 10 доме</div>
        <div className="text-xs text-slate-500 mt-1" data-id="element-17106">Карьера и статус</div>
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.2
    }} className="bg-slate-100 rounded-xl p-4 border border-slate-200" data-id="element-17107">
        <div className="flex items-center gap-2 mb-2" data-id="element-17108">
          <div className="w-6 h-6 rounded-full bg-slate-200 text-slate-700 flex items-center justify-center font-bold" data-id="element-17109">
            ♄
          </div>
          <span className="text-xs font-bold text-slate-600 uppercase tracking-wider" data-id="element-17110">
            Сатурн
          </span>
        </div>
        <div className="text-lg font-bold text-slate-900" data-id="element-17111">в 7 доме</div>
        <div className="text-xs text-slate-500 mt-1" data-id="element-17112">Уроки в отношениях</div>
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.3
    }} className="bg-cyan-50 rounded-xl p-4 border border-cyan-100" data-id="element-17113">
        <div className="flex items-center gap-2 mb-2" data-id="element-17114">
          <div className="w-6 h-6 rounded-full bg-cyan-200 text-cyan-700 flex items-center justify-center font-bold" data-id="element-17115">
            ♅
          </div>
          <span className="text-xs font-bold text-cyan-600 uppercase tracking-wider" data-id="element-17116">
            Уран
          </span>
        </div>
        <div className="text-lg font-bold text-slate-900" data-id="element-17117">в 1 доме</div>
        <div className="text-xs text-slate-500 mt-1" data-id="element-17118">Трансформация</div>
      </motion.div>
    </div>;
  // ═══════════════════════════════════════════
  // MAIN RENDER
  // ═══════════════════════════════════════════
  return <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden max-w-5xl mx-auto" data-id="element-17119">
      {/* Report Header */}
      <div className={`bg-gradient-to-r ${config.gradient} text-white p-6 sm:p-8`} data-id="element-17120">
        <div className="flex items-center justify-between mb-4" data-id="element-17121">
          <div className="flex items-center gap-2 flex-wrap" data-id="element-17122">
            <Badge variant="neutral" className="bg-white/20 text-white border-white/30" data-id="element-17123">
              Beta отчёт
            </Badge>
            <Badge variant="neutral" className="bg-white/15 text-white/80 border-white/20 text-xs" data-id="element-17124">
              Полный отчёт:{' '}
              {serviceType === 'combo' ? '62' : serviceType === 'solar' ? '38' : serviceType === 'compatibility' ? '32' : serviceType === 'forecast' ? '30' : '35'}{' '}
              стр.
            </Badge>
          </div>
          <span className="text-xs text-slate-300" data-id="element-17125">
            Сгенерировано: {new Date().toLocaleDateString('ru-RU')}
          </span>
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold mb-2" data-id="element-17126">{config.title}</h1>
        <p className="text-slate-200 text-sm" data-id="element-17127">{config.subtitle}</p>
      </div>

      {/* Birth Data Section */}
      <div className="p-6 sm:p-8 border-b border-slate-100" data-id="element-17128">
        <h2 className="text-lg font-bold text-slate-900 mb-4" data-id="element-17129">
          Вводные данные
        </h2>

        <div className="bg-slate-50 rounded-xl p-4 sm:p-6 mb-4" data-id="element-17130">
          <div className="text-lg font-bold text-slate-900 mb-3" data-id="element-17131">
            Алексей Петров
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-17132">
            <div className="flex items-center gap-2 text-sm" data-id="element-17133">
              <CalendarIcon className={`w-4 h-4 text-${config.accentColor}-500`} data-id="element-17134" />
              <span className="text-slate-700" data-id="element-17135">15 августа 1992</span>
            </div>
            <div className="flex items-center gap-2 text-sm" data-id="element-17136">
              <ClockIcon className={`w-4 h-4 text-${config.accentColor}-500`} data-id="element-17137" />
              <span className="text-slate-700" data-id="element-17138">14:30</span>
            </div>
            <div className="flex items-center gap-2 text-sm" data-id="element-17139">
              <MapPinIcon className={`w-4 h-4 text-${config.accentColor}-500`} data-id="element-17140" />
              <span className="text-slate-700" data-id="element-17141">Москва, Россия</span>
            </div>
          </div>
        </div>

        {config.person2 && <div className="bg-slate-50 rounded-xl p-4 sm:p-6" data-id="element-17142">
            <div className="text-lg font-bold text-slate-900 mb-3" data-id="element-17143">
              {config.person2.name}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-17144">
              <div className="flex items-center gap-2 text-sm" data-id="element-17145">
                <CalendarIcon className={`w-4 h-4 text-${config.accentColor}-500`} data-id="element-17146" />
                <span className="text-slate-700" data-id="element-17147">{config.person2.date}</span>
              </div>
              <div className="flex items-center gap-2 text-sm" data-id="element-17148">
                <ClockIcon className={`w-4 h-4 text-${config.accentColor}-500`} data-id="element-17149" />
                <span className="text-slate-700" data-id="element-17150">{config.person2.time}</span>
              </div>
              <div className="flex items-center gap-2 text-sm" data-id="element-17151">
                <MapPinIcon className={`w-4 h-4 text-${config.accentColor}-500`} data-id="element-17152" />
                <span className="text-slate-700" data-id="element-17153">{config.person2.place}</span>
              </div>
            </div>
          </div>}

        {config.extraInfo && <div className={`mt-4 inline-block px-3 py-1.5 bg-${config.accentColor}-50 text-${config.accentColor}-700 rounded-lg text-sm font-medium`} data-id="element-17154">
            {config.extraInfo}
          </div>}
      </div>

      {/* Highlights Section */}
      <div className="p-6 sm:p-8 border-b border-slate-100" data-id="element-17155">
        <h2 className="text-lg font-bold text-slate-900 mb-4" data-id="element-17156">
          {serviceType === 'natal' ? 'Ваша «Большая тройка»' : serviceType === 'compatibility' ? 'Ключевые показатели' : 'Главные акценты'}
        </h2>

        {serviceType === 'natal' && renderNatalHighlights()}
        {serviceType === 'solar' && renderSolarHighlights()}
        {serviceType === 'compatibility' && renderCompatibilityHighlights()}
        {serviceType === 'combo' && renderComboHighlights()}
        {serviceType === 'forecast' && renderForecastHighlights()}
      </div>

      {/* Summary Text */}
      <div className="p-6 sm:p-8 border-b border-slate-100" data-id="element-17157">
        <h2 className="text-lg font-bold text-slate-900 mb-4" data-id="element-17158">
          Краткое резюме
        </h2>
        <div className="prose prose-slate max-w-none" data-id="element-17159">
          {config.summary.split('\n\n').map((paragraph, i) => <p key={i} className="text-slate-600 leading-relaxed mb-4 last:mb-0" data-id="element-17160">
              {paragraph}
            </p>)}
        </div>
      </div>

      {/* Locked Premium Content */}
      <div className="p-6 sm:p-8 bg-slate-50 relative" data-id="element-17161">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-slate-50/80 to-slate-50 pointer-events-none" data-id="element-17162" />

        <div className="relative" data-id="element-17163">
          <div className="flex items-center gap-3 mb-6" data-id="element-17164">
            <div className={`w-10 h-10 rounded-full bg-${config.accentColor}-100 text-${config.accentColor}-600 flex items-center justify-center`} data-id="element-17165">
              <LockIcon className="w-5 h-5" data-id="element-17166" />
            </div>
            <div data-id="element-17167">
              <h2 className="text-lg font-bold text-slate-900" data-id="element-17168">
                Полный Premium отчёт
              </h2>
              <p className="text-sm text-slate-500" data-id="element-17169">
                Разблокируйте все разделы анализа
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6" data-id="element-17170">
            {config.lockedSections.map((section, i) => <motion.div key={i} initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.1 * i
          }} className="flex items-center gap-2 text-sm text-slate-400 bg-white/60 rounded-lg px-3 py-2 border border-slate-200/50" data-id="element-17171">
                <LockIcon className="w-3.5 h-3.5 flex-shrink-0" data-id="element-17172" />
                <span className="blur-[1px]" data-id="element-17173">{section}</span>
              </motion.div>)}
          </div>

          <Card padding="md" className={`bg-white border-${config.accentColor}-200 shadow-lg`} data-id="element-17174">
            <div className="flex flex-col sm:flex-row items-center gap-4" data-id="element-17175">
              <div className="flex-1 text-center sm:text-left" data-id="element-17176">
                <div className="flex items-center justify-center sm:justify-start gap-2 mb-1" data-id="element-17177">
                  <SparklesIcon className={`w-5 h-5 text-${config.accentColor}-500`} data-id="element-17178" />
                  <span className="font-bold text-slate-900" data-id="element-17179">
                    Разблокировать полный отчёт
                  </span>
                </div>
                <p className="text-sm text-slate-500" data-id="element-17180">
                  {serviceType === 'combo' ? '60+' : '30-40'} страниц глубокого
                  анализа
                </p>
              </div>
              <div className="flex items-center gap-4" data-id="element-17181">
                <span className="text-2xl font-bold text-slate-900" data-id="element-17182">
                  {serviceType === 'combo' ? '6 900 ₽' : serviceType === 'compatibility' ? '4 900 ₽' : '3 900 ₽'}
                </span>
                {onNavigate && <Button variant="primary" onClick={() => {
                const priceMap: Record<ServiceType, number> = {
                  natal: 3900,
                  solar: 3900,
                  compatibility: 4900,
                  combo: 6900,
                  forecast: 3900
                };
                addItem({
                  id: `premium-${serviceType}-${Date.now()}`,
                  name: `Premium отчёт: ${config.title}`,
                  description: config.subtitle,
                  price: priceMap[serviceType],
                  type: 'report',
                  format: 'PDF-отчёт',
                  badges: [{
                    label: 'Premium',
                    variant: 'pro'
                  }]
                });
                onNavigate('checkout');
              }} className={`bg-${config.accentColor}-600 hover:bg-${config.accentColor}-700`} data-id="element-17183">
                    Купить
                    <ArrowRightIcon className="ml-2 w-4 h-4" data-id="element-17184" />
                  </Button>}
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Footer */}
      <div className="p-6 bg-slate-50 border-t border-slate-200" data-id="element-17185">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-17186">
          <div className="text-center sm:text-left" data-id="element-17187">
            <p className="text-sm font-medium text-slate-700" data-id="element-17188">
              Astriqo • Beta отчёт
            </p>
            <p className="text-xs text-slate-500 mt-1" data-id="element-17189">
              Для полного анализа закажите Premium отчёт или консультацию
              астролога
            </p>
          </div>
          {onNavigate && <button onClick={() => onNavigate('service-consultation')} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-slate-200 hover:bg-slate-100 text-slate-600 hover:text-slate-800 transition-colors text-sm font-medium" data-id="element-17190">
              <SparklesIcon className="w-4 h-4" data-id="element-17191" />
              Заказать консультацию
            </button>}
        </div>
      </div>
    </div>;
}